import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  InventoryConversionFactorUpdate,
  InventoryCustomUOMEvent,
  InventoryGroupDropdownEvent,
  InventoryItemSelectionUpdate,
  InventoryItemUOMEvent,
  InventoryLineNumberEvent,
  InventoryPriceUpdate,
  InventoryToolTipMessaging,
  InventoryUOMDropdownEvent,
} from '@inventory/models/edit-inventory.model';
import {
  InventoryProductViewModel,
  InventoryViewModel,
} from '@inventory/models/inventory-view.model';
import { CreateInventoryValidationErrors } from '@inventory/models/validation-errors-model';
import { InventoryService } from '@inventory/services/inventory.service';
import {
  ModalController,
  Platform,
  PopoverController,
  PopoverOptions,
  ToastController,
  ViewDidLeave,
  ViewWillEnter,
  ViewWillLeave,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { Crumb } from '@shared/components/breadcrumbs/breadcrumbs.component';
import {
  SortDirections,
  SortableSectionHeader,
} from '@shared/components/sortable-section/models/sortable-section.model';
import { PageTitle } from '@shared/constants/page-titles.enum';
import { PATHS } from '@shared/constants/paths';
import { FEATURES } from '@shared/constants/splitio-features';
import { SortOption, SortSelection } from '@shared/models/sort.model';
import { ToggleNewSearchService } from '@shared/services/toggle-new-search/toggle-new-search.service';
import { InventoryGroup, InventoryStatus } from '@usf/inventory-types';
import {
  InventoryGroupHeader,
  InventoryItemModalOptions,
  InventoryWorksheetCountSortOptions,
  InventoryWorksheetEditSortOptions,
} from '@usf/ngrx-inventory';
import { InventorySort } from '@usf/ngrx-inventory/lib/models/inventory/inventory.model';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { first, take, tap } from 'rxjs/operators';
import { Button } from '../../../shared/models/ellipsis-option.model';
import { InventoryHotkeyService } from '../../../shared/services/hotkey/inventory-hotkey.service';
import {
  InventoryActions,
  InventoryWorksheetMode,
} from '../../constants/inventory-constants';
import { InventorySortableSectionHeaderUpdate } from '../../models/inventory-sortable-section.model';
import { InventoryWorksheetViewModel } from '../../models/inventory-worksheet-view-model.model';
import { TooltipCatchweightComponent } from './components/tooltip-catchweight/tooltip-catchweight.component';
import { InventoryWorksheetService } from './services/inventory-worksheet.service';

@Component({
  selector: 'app-inventory-worksheet',
  templateUrl: './inventory-worksheet.page.html',
  styleUrls: ['./inventory-worksheet.page.scss'],
})
export class InventoryWorksheetPage
  implements ViewWillEnter, ViewWillLeave, ViewDidLeave
{
  // Enums
  public InventoryWorksheetMode = InventoryWorksheetMode;
  readonly InventoryActions = InventoryActions;
  readonly InventoryStatus = InventoryStatus;
  // Observables
  inventoryWorksheetViewModel$: Observable<InventoryWorksheetViewModel>;
  inventoryWorksheetLoadCheck$;
  downloadFlag$: Observable<boolean>;
  validation$: Observable<CreateInventoryValidationErrors>;
  // Subscriptions
  routeParamsSub$: Subscription = new Subscription();
  onlineSubscription: Subscription = new Subscription();
  isNewSearchEnabledSubscription$: Subscription;

  // Component Variables
  breadcrumbs: Crumb[];
  inventoryId: string;
  searchKey = '';
  newInventoryName: string;
  isAndroidTouchOrWindows: boolean;
  isAndroidTouch: boolean;
  isNewSearchEnabled = false;

  private _tooltip: HTMLIonPopoverElement;
  hideToolTipTimeout: any;
  hideTime = 5000;
  showToolTipTimeout: any;
  showTime = 250;
  tooltipOptions: PopoverOptions;

  groupNameToChange = '';

  interval: any;
  focusedItem: InventoryProductViewModel;
  sortSelection: SortSelection;
  countModeSortOptions: SortOption[];
  editModeSortOptions: SortOption[];
  readonly defaultSortSelection: SortSelection = {
    fieldName: InventoryWorksheetCountSortOptions.lineNum,
    sortValue: SortDirections.asc,
  };
  isSortApplied = false;

  constructor(
    private route: ActivatedRoute,
    private modalController: ModalController,
    private popoverController: PopoverController,
    private toastController: ToastController,
    private panAppState: PanAppState,
    readonly platform: Platform,
    readonly platformService: PlatformService,
    readonly translateService: TranslateService,
    readonly inventoryWorksheetService: InventoryWorksheetService,
    private inventoryService: InventoryService,
    private inventoryHotkeyService: InventoryHotkeyService,
    private toggleNewSearchService: ToggleNewSearchService,
  ) {
    var homeCrumbUrl = this.platformService.isTouch.value
      ? '/touch/home'
      : '/desktop/home';
    this.breadcrumbs = [
      {
        label: 'i18n.breadcrumbs.home',
        url: homeCrumbUrl,
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.inventory',
        url: '/' + PATHS.INVENTORY,
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.inventoryWorksheet',
        url: '',
        active: true,
      },
    ];
  }

  ionViewWillEnter() {
    this.isNewSearchEnabledSubscription$ = this.toggleNewSearchService
      .isNewSearchEnabled()
      .subscribe(newSearchEnabled => {
        this.inventoryWorksheetService.isNewSearchEnabled = newSearchEnabled;
      });
    //Retain the current mode if coming back from search page
    this.panAppState.previousUrl$.pipe(take(1)).subscribe(url => {
      if (url.indexOf('/search?') === -1) {
        this.setDefaultInventoryWorksheetMode();
      }
    });
    this.resetSearch();
    this.setDefaultInventoryLandingGroup();
    this.setPageTitle();
    this.setDisableCompleteButton();
    this.setIsAndroidTouch();
    this.resetSearchKey();
    this.getInventoryWorksheet();
    this.getValidation();
    this.setInitialSortSelection();
    // Need inventory ID set by getInventoryWorksheet
    this.onlineSubscription = this.inventoryWorksheetService
      .onlineWithDelay(this.inventoryId)
      .subscribe();
    this.downloadFlag$ = this.panAppState.feature$([
      FEATURES.split_global_inventory_worksheet_download.name,
    ]);
    this.countModeSortOptions =
      this.inventoryWorksheetService.createCountModeSortOptions();
    this.editModeSortOptions =
      this.inventoryWorksheetService.createEditModeSortOptions();
  }

  setDefaultInventoryLandingGroup() {
    const defaultLandingGroup: InventoryGroupHeader = {
      groupName: 'All',
    };
    this.inventoryWorksheetService.updateSelectedGroup(defaultLandingGroup);
    this.inventoryService.addNonUSFProdFromInventoryWorksheet = false;
  }

  onGroupDragDropped(
    inventory: InventoryViewModel,
    event: { previousIndex: number; currentIndex: number },
  ) {
    if (event.previousIndex !== event.currentIndex) {
      this.inventoryWorksheetService.moveGroup(
        inventory.inventoryId,
        event.previousIndex,
        event.currentIndex,
      );
    }
  }

  getValidation() {
    this.inventoryWorksheetService.resetValidation();
    this.validation$ = this.inventoryWorksheetService.nameDateValidations$();
  }

  setPageTitle() {
    // Temp fix for back button on page, header component removes back button when pageTitle is 'Inventory'
    // Can remove once we move inventory under the more tab as part of navigation changes
    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.inventory) + ' ',
    );
  }

  setDefaultInventoryWorksheetMode() {
    this.inventoryWorksheetService.updateInventoryWorksheetMode(
      InventoryWorksheetMode.count,
    );
  }

  setDisableCompleteButton() {
    this.inventoryWorksheetService.disableCompleteButton = false;
  }

  setIsAndroidTouch() {
    this.isAndroidTouch =
      this.platform.is('android') && this.platformService.isTouch.value
        ? true
        : false;
  }

  resetSearchKey() {
    if (this.searchKey) {
      this.searchKey = '';
    }
  }

  setInitialSortSelection() {
    this.sortSelection = this.defaultSortSelection;
  }

  // INVENTORY ACTIONS
  getInventoryWorksheet() {
    this.routeParamsSub$ = this.route.params.subscribe(params => {
      const inventoryId = params['inventoryId'];

      if (inventoryId) {
        this.inventoryId = inventoryId;
        this.inventoryWorksheetService.activeInventoryId = inventoryId;
        this.inventoryWorksheetViewModel$ = this.inventoryWorksheetService
          .inventoryWorksheetView$(
            this.platformService.platformType,
            this.inventoryId,
          )
          .pipe(
            tap((viewModel: InventoryWorksheetViewModel) =>
              this.setHotkeyValues(viewModel),
            ),
          );
        this.inventoryWorksheetService.resetInventoryItemSelections(
          inventoryId,
        );
        this.inventoryWorksheetLoadCheck$ =
          this.inventoryWorksheetService.inventoryService.runInventoryDisplayCheck(
            inventoryId,
          );
        this.inventoryWorksheetLoadCheck$.pipe(first()).subscribe(worksheet => {
          this.inventoryWorksheetService.inventoryService.traceInventoryDisplayEnd(
            worksheet.inventoryId,
            worksheet?.inventoryName +
              ' - ' +
              this.inventoryWorksheetService.transformDate(
                worksheet?.inventoryDate,
              ),
            true,
          );
          this.inventoryWorksheetService.trackInventoryWorksheetLoaded(
            this.inventoryId,
          );
        });
      }
    });
  }

  setHotkeyValues(viewModel: InventoryWorksheetViewModel) {
    this.inventoryHotkeyService.setItemHeights(
      viewModel.inventory?.itemHeights,
    );
    this.inventoryHotkeyService.setIsEditMode(
      viewModel.inventory?.inventoryOptions?.inventoryWorksheetMode ===
        InventoryWorksheetMode.edit,
    );
    this.inventoryHotkeyService.setInventoryCardIds(
      viewModel.inventory?.inventoryCardIds,
    );
    this.inventoryHotkeyService.setInventoryId(
      viewModel.inventory?.inventoryId,
    );
  }

  updateInventoryWorksheetMode(event: any) {
    this.inventoryWorksheetService.updateInEditNameMode(false);
    this.inventoryWorksheetService.updateInEditGroupsMode(false);
    this.inventoryWorksheetService.updateInventoryWorksheetMode(
      event?.target?.value,
    );
    event?.target?.value === InventoryWorksheetMode.count
      ? this.inventoryWorksheetService.resetInventoryWorksheetCountSort(
          this.inventoryId,
        )
      : this.inventoryWorksheetService.resetInventoryWorksheetEditSort(
          this.inventoryId,
        );
    this.setInitialSortSelection();
    this.setSortApplied();
  }

  updateInventoryWorksheetSort(
    mode: string,
    event: InventorySortableSectionHeaderUpdate,
  ) {
    let sortDirection = event?.sortDirection === 'asc' ? 'desc' : 'asc';

    mode === InventoryWorksheetMode.count
      ? this.inventoryWorksheetService.updateInventoryWorksheetCountSort(
          this.inventoryId,
          event.groupName,
          event.headerName,
          sortDirection,
        )
      : this.inventoryWorksheetService.updateInventoryWorksheetEditSort(
          this.inventoryId,
          event.groupName,
          event.headerName,
          sortDirection,
        );
  }

  resetInventoryWorksheetSortFromModal(mode: string, selectedGroup: string) {
    this.setInitialSortSelection();
    if (selectedGroup === 'All') {
      mode === InventoryWorksheetMode.count
        ? this.inventoryWorksheetService.resetInventoryWorksheetCountSort(
            this.inventoryId,
          )
        : this.inventoryWorksheetService.resetInventoryWorksheetEditSort(
            this.inventoryId,
          );
    } else {
      mode === InventoryWorksheetMode.count
        ? this.inventoryWorksheetService.updateInventoryWorksheetCountSort(
            this.inventoryId,
            selectedGroup,
            InventoryWorksheetCountSortOptions.lineNum,
            SortDirections.asc,
          )
        : this.inventoryWorksheetService.updateInventoryWorksheetEditSort(
            this.inventoryId,
            selectedGroup,
            InventoryWorksheetEditSortOptions.lineNum,
            SortDirections.asc,
          );
    }
    this.setSortApplied();
  }

  toggleEditNameIcon(inEditNameMode: boolean) {
    this.inventoryWorksheetService.updateInEditNameMode(
      inEditNameMode ? false : true,
    );
  }

  getInventoryWorksheetNameDateExists(event: any, date: string | Date) {
    this.newInventoryName = event?.target?.value;
    this.inventoryWorksheetService.getInventoryNameAndDateExists(
      event?.target?.value,
      date,
    );
  }

  onSaveName() {
    this.inventoryWorksheetService.updateInEditNameMode(false);
    this.inventoryWorksheetService.editInventoryName(
      this.inventoryId,
      this.newInventoryName,
    );
  }

  search() {
    this.inventoryWorksheetService.searchInInventoryWorksheet(this.searchKey);
  }

  async resetSearch() {
    setTimeout(this.searchTimeout, 50);
  }

  searchTimeout = () => {
    this.searchKey = '';
    this.search();
  };

  async openPriceOptions(event: Event) {
    event.stopPropagation();
    this.inventoryWorksheetService.trackClickUpdateAllPricesButton(
      this.inventoryId,
    );
    const buttons: Button[] = [
      {
        text: this.translateService.instant(
          'i18n.inventory.inventoryWorksheet.pricing.mp',
        ),
        handler: () => {
          this.inventoryWorksheetService.updateSelectedPrice(
            'MP',
            this.inventoryId,
          );
          this.inventoryWorksheetService.dismissSelection();
        },
      },
      {
        text: this.translateService.instant(
          'i18n.inventory.inventoryWorksheet.pricing.lp',
        ),
        handler: () => {
          this.inventoryWorksheetService.updateSelectedPrice(
            'LP',
            this.inventoryId,
          );
          this.inventoryWorksheetService.dismissSelection();
        },
      },
    ];

    this.platformService.isTouch.value
      ? this.inventoryWorksheetService.openSelectionsTouch(buttons)
      : this.inventoryWorksheetService.openSelectionsDesktop(event, buttons);
  }

  completeInventory() {
    this.inventoryWorksheetService.disableCompleteButton = true;
    this.inventoryWorksheetService.completeInventory(this.inventoryId);
  }

  // INVENTORY GROUP ACTIONS
  updateSelectedGroup(
    selectedGroup: InventoryGroupHeader,
    currentSort: InventorySort,
    index: number,
  ) {
    this.inventoryWorksheetService.resetInventoryItemSelections(
      this.inventoryId,
    );
    this.inventoryWorksheetService.updateSelectedGroup(selectedGroup);
    this.getSortSelection(selectedGroup.groupName, currentSort);
    this.scrollToAllGroups();
    this.scrollToSingleGroup(index);
  }

  addGroup(inventory: InventoryViewModel) {
    this.inventoryWorksheetService.disableAddGroupButton = true;
    this.inventoryWorksheetService.createNewGroup(inventory);
  }

  deleteGroup(inventory: InventoryViewModel, group: InventoryGroupHeader) {
    this.inventoryWorksheetService.deleteGroup(inventory, group);
  }

  toggleEditGroupsIcon(inEditGroupsMode: boolean) {
    this.inventoryWorksheetService.updateInEditGroupsMode(
      inEditGroupsMode ? false : true,
    );
  }

  revertGroupName(group: InventoryGroup) {
    this.groupNameToChange = group.groupName;
  }

  updateGroupName(inventory: InventoryViewModel, group: InventoryGroup) {
    if (group.groupName === this.groupNameToChange) {
      return;
    }
    const allText = this.translateService.instant(
      'i18n.inventory.inventoryWorksheet.all',
    );

    let newNameIsAll = allText.toUpperCase() === group.groupName.toUpperCase();
    let nameIsEmpty = group.groupName.length === 0;

    // change group name successfully:
    if (
      !this.inventoryWorksheetService.groupNameExists(
        inventory,
        group.groupName,
      ) &&
      !newNameIsAll &&
      !nameIsEmpty
    ) {
      this.inventoryWorksheetService.updateGroupName(
        inventory,
        { ...group, groupName: this.groupNameToChange },
        group.groupName,
      );
    }
    // error state: display error toast and revert to previously saved name:
    else {
      if (nameIsEmpty) {
        // no empty group names allowed:
        this.inventoryWorksheetService.displayEmptyGroupErrorToast();

        // revert name back to preexisting group name:
        this.inventoryWorksheetService.updateGroupName(
          inventory,
          { ...group, groupName: this.groupNameToChange },
          this.groupNameToChange,
        );
      } else {
        // display banner message with error text:
        this.inventoryWorksheetService.displayRenameGroupErrorToast();

        // revert name back to preexisting group name:
        this.inventoryWorksheetService.updateGroupName(
          inventory,
          { ...group, groupName: this.groupNameToChange },
          this.groupNameToChange,
        );
      }
    }
  }

  // INVENTORY ITEM ACTIONS
  async selectInventoryItem(itemSelectionUpdate: InventoryItemSelectionUpdate) {
    this.inventoryWorksheetService.selectInventoryItem(itemSelectionUpdate);
  }

  async moveInventoryItemToNewGroup(
    groupDropdownEvent: InventoryGroupDropdownEvent,
  ) {
    this.inventoryHotkeyService.onDropdownClick(groupDropdownEvent.event);
    this.inventoryWorksheetService.openInventoryItemGroupDropdown(
      groupDropdownEvent,
    );
  }

  async updateInventoryItemUOM(uomDropdownEvent: InventoryUOMDropdownEvent) {
    this.inventoryHotkeyService.onDropdownClick(uomDropdownEvent.event);
    let buttons: Button[];
    if (uomDropdownEvent.isCatchWeight) {
      buttons = [
        {
          text: this.translateService.instant(
            'i18n.inventory.inventoryWorksheet.uom.pound',
          ),
          handler: () => this.updateUOMForInventoryItem(uomDropdownEvent, 'LB'),
        },
      ];
    } else {
      buttons = [
        {
          text: this.translateService.instant(
            'i18n.inventory.inventoryWorksheet.uom.each',
          ),
          handler: () => this.updateUOMForInventoryItem(uomDropdownEvent, 'EA'),
        },
        {
          text: this.translateService.instant(
            'i18n.inventory.inventoryWorksheet.uom.pound',
          ),
          handler: () => this.updateUOMForInventoryItem(uomDropdownEvent, 'LB'),
        },
        {
          text: this.translateService.instant(
            'i18n.inventory.inventoryWorksheet.uom.ounce',
          ),
          handler: () => this.updateUOMForInventoryItem(uomDropdownEvent, 'OZ'),
        },
        {
          text: this.translateService.instant(
            'i18n.inventory.inventoryWorksheet.uom.grams',
          ),
          handler: () => this.updateUOMForInventoryItem(uomDropdownEvent, 'G'),
        },
        {
          text: this.translateService.instant(
            'i18n.inventory.inventoryWorksheet.uom.gallon',
          ),
          handler: () => this.updateUOMForInventoryItem(uomDropdownEvent, 'GA'),
        },
        {
          text: this.translateService.instant(
            'i18n.inventory.inventoryWorksheet.uom.batch',
          ),
          handler: () =>
            this.updateUOMForInventoryItem(uomDropdownEvent, 'Batch'),
        },
        {
          text: this.translateService.instant(
            'i18n.inventory.inventoryWorksheet.uom.piece',
          ),
          handler: () =>
            this.updateUOMForInventoryItem(uomDropdownEvent, 'Piece'),
        },
      ];
      if (uomDropdownEvent.inventoryItem.customUOM) {
        buttons.push({
          text: uomDropdownEvent.inventoryItem.customUOM,
          cssClass: 'bold-font-weight',
          handler: () =>
            this.updateUOMForInventoryItem(
              uomDropdownEvent,
              uomDropdownEvent.inventoryItem.customUOM,
            ),
        });
        buttons.push({
          text: 'Edit Custom',
          handler: () =>
            this.customUOMModalOpenRequest(uomDropdownEvent.inventoryItem),
        });
      } else {
        buttons.push({
          text: 'Add Custom',
          handler: () =>
            this.customUOMModalOpenRequest(uomDropdownEvent.inventoryItem),
        });
      }
    }

    this.platformService.isTouch.value
      ? this.inventoryWorksheetService.openSelectionsTouch(buttons)
      : this.inventoryWorksheetService.openSelectionsDesktop(
          uomDropdownEvent.event,
          buttons,
        );
  }

  updateUOMForInventoryItem(event: InventoryUOMDropdownEvent, unit: string) {
    this.inventoryWorksheetService.updateInventoryItemUOM({
      inventoryItemId: event.inventoryItemId,
      inventoryId: event.inventoryId,
      groupName: event.group,
      unit,
    });
    this.inventoryWorksheetService.dismissSelection();
  }

  updateCustomUOMForInventoryItem(event: InventoryCustomUOMEvent) {
    this.inventoryWorksheetService.updateCustomUnit(event, this.inventoryId);
    this.openOrCloseCustomUOMModal(false);
  }

  updateInventoryItemQuantity(
    quantityUpdate: any,
    inventory?: InventoryViewModel,
  ) {
    this.inventoryWorksheetService.updateInventoryItemQuantity(
      quantityUpdate,
      inventory,
    );
  }

  updateInventoryPriceOverride(priceOverrideUpdate: InventoryPriceUpdate) {
    this.inventoryWorksheetService.updateInventoryItemPriceOverride(
      priceOverrideUpdate,
    );
  }

  async showToolTip(toolTip: InventoryToolTipMessaging) {
    if (this.showToolTipTimeout !== undefined) {
      return;
    }
    this.showToolTipTimeout = setTimeout(async () => {
      if (this.hideToolTipTimeout !== undefined) {
        clearTimeout(this.hideToolTipTimeout);
        this.hideToolTipTimeout = undefined;
      }

      this.tooltipOptions = {
        component: TooltipCatchweightComponent,
        componentProps: {
          message: toolTip.isCatchWeightWarning
            ? 'Catch weight item counted by weight, not cases'
            : 'Pricing not found, please add value in the Price column',
        },
        showBackdrop: true,
        cssClass: toolTip.isCatchWeightWarning
          ? 'ellipsis-options-popover-desktop'
          : 'inventory-warning-popover',
        event: toolTip.event$,
        mode: 'ios',
        backdropDismiss: true,
      };

      this._tooltip = await this.popoverController.create(this.tooltipOptions);
      await this._tooltip.present();
      this.hideToolTipTimeout = setTimeout(() => {
        this._tooltip?.dismiss();
        this._tooltip = undefined;
        this.hideToolTipTimeout = undefined;
      }, this.hideTime);
      this.showToolTipTimeout = undefined;
    }, this.showTime);
  }

  async hideToolTip() {
    if (this.showToolTipTimeout !== undefined) {
      clearTimeout(this.showToolTipTimeout);
      this.showToolTipTimeout = undefined;
    }
  }

  updateInventoryConversionFactor(
    conversionFactorUpdate: InventoryConversionFactorUpdate,
  ) {
    this.inventoryWorksheetService.updateInventoryItemConversionFactor(
      conversionFactorUpdate,
    );
  }

  openInventoryItemModal(
    item: InventoryProductViewModel,
    inventory: InventoryViewModel,
  ) {
    this.inventoryWorksheetService.openInventoryItemModal(item, inventory);
  }

  closeInventoryItemModal(
    didSave: boolean,
    inventoryItemModalOptions: InventoryItemModalOptions,
  ) {
    if (didSave) {
      this.inventoryWorksheetService.saveItemChangesFromInventoryItemModal(
        inventoryItemModalOptions,
      );
    }
    if (inventoryItemModalOptions.isOpen) {
      this.inventoryWorksheetService.closeInventoryItemModal();
    }
  }

  informationModalOpenRequest(inventoryItem: InventoryProductViewModel) {
    this.focusedItem = inventoryItem;
    this.inventoryWorksheetService.openOrCloseInventoryInformationModal(true);
  }

  openOrCloseInformationModal(shouldOpen: boolean) {
    if (!shouldOpen) {
      this.focusedItem = undefined;
    }
    this.inventoryWorksheetService.openOrCloseInventoryInformationModal(
      shouldOpen,
    );
  }

  customUOMModalOpenRequest(inventoryItem: InventoryProductViewModel) {
    this.focusedItem = inventoryItem;
    this.inventoryWorksheetService.dismissSelection();
    this.openOrCloseCustomUOMModal(true);
  }

  openOrCloseCustomUOMModal(shouldOpen: boolean) {
    if (!shouldOpen) {
      this.focusedItem = undefined;
    }
    this.inventoryWorksheetService.openOrCloseInventoryCustomUOMModal(
      shouldOpen,
    );
  }

  changeInventoryItemModalOptions(event: InventoryItemModalOptions) {
    this.inventoryWorksheetService.changeInventoryItemModalOptions(event);
  }

  openUnitPopoverInItemModal(event: InventoryItemUOMEvent) {
    this.inventoryWorksheetService.openUnitPopoverInItemModal(event);
  }

  deselectAllProducts() {
    this.inventoryWorksheetService.resetInventoryItemSelections(
      this.inventoryId,
    );
  }

  selectAllProducts(inventory: InventoryViewModel) {
    this.inventoryWorksheetService.selectAllItemsInGroup(inventory);
  }

  resetPreviousHotkeyId() {
    this.inventoryHotkeyService.setPreviousId('');
  }

  handleNonUSFNavigate() {
    this.inventoryWorksheetService.closeInventoryItemModal();
    this.inventoryWorksheetService.navigateToNonUSF();
  }

  handleLineNumberChange(
    inventoryLineNumberEvent: InventoryLineNumberEvent,
    inventory: InventoryViewModel,
  ) {
    this.inventoryWorksheetService.moveInventoryItemByLineNumber(
      inventoryLineNumberEvent,
      inventory?.numProducts,
    );
  }

  openInventoryDownloadOptions(event: any, inventory: InventoryViewModel) {
    // Other download options commented out while they are not functional
    const buttons: Button[] = [
      {
        text: this.translateService.instant(
          'i18n.inventory.downloadInventoryModal.downloadInventoryWorksheet',
        ),
        handler: () => {
          this.inventoryService.openDownloadInventoryModal(inventory);
        },
      },
      // {
      //   text: this.translateService.instant(
      //     'i18n.inventory.downloadInventoryCostReport',
      //   ),
      //   handler: () => {},
      // },
      // {
      //   text: this.translateService.instant(
      //     'i18n.inventory.downloadExtendedCostReport',
      //   ),
      //   handler: () => {},
      // },
      // {
      //   text: this.translateService.instant(
      //     'i18n.inventory.downloadCostComparisonReport',
      //   ),
      //   handler: () => {},
      // },
    ];
    if (
      inventory.status === InventoryStatus.COMPLETED &&
      inventory.completedDtm
    ) {
      buttons.push({
        text: this.translateService.instant(
          'i18n.inventory.downloadCostReport',
        ),
        handler: () => {
          combineLatest([
            this.inventoryService.selectInventoriesForCostReport$(),
            this.inventoryService.selectShowGl$(),
          ])
            .pipe(take(1))
            .subscribe(([inventories, glFlag]) => {
              this.inventoryService.handleShowCostReport(
                inventories,
                glFlag,
                inventory,
              );
            });
        },
      });
    } else {
      buttons.push({
        text: this.translateService.instant(
          'i18n.inventory.pleaseCompleteForReport',
        ),
        cssClass: 'popover-information',
        nonButton: true,
        handler: () => false,
      });
    }
    this.platformService.isTouch.value
      ? this.inventoryWorksheetService.openSelectionsTouch(buttons)
      : this.inventoryWorksheetService.openSelectionsDesktop(event, buttons);
  }

  openOrCloseSortModal(shouldOpen: boolean) {
    this.inventoryWorksheetService.openOrCloseSortModal(shouldOpen);
  }

  getSortSelection(selectedGroup: string, currentSort: InventorySort) {
    this.sortSelection = this.inventoryWorksheetService.getSortSelection(
      selectedGroup,
      currentSort,
    );
    this.setSortApplied();
    return this.sortSelection;
  }

  updateInventoryWorksheetSortFromModal(
    mode: string,
    selectedGroup: string,
    sortableSection: SortableSectionHeader,
  ) {
    const inventorySortUpdate: InventorySortableSectionHeaderUpdate = {
      groupName: selectedGroup,
      ...sortableSection,
    };
    this.sortSelection = {
      fieldName: inventorySortUpdate.headerName,
      sortValue: inventorySortUpdate.sortDirection,
    };
    this.setSortApplied();

    mode === InventoryWorksheetMode.count
      ? this.inventoryWorksheetService.updateInventoryWorksheetCountSort(
          this.inventoryId,
          inventorySortUpdate.groupName,
          inventorySortUpdate.headerName,
          inventorySortUpdate.sortDirection,
        )
      : this.inventoryWorksheetService.updateInventoryWorksheetEditSort(
          this.inventoryId,
          inventorySortUpdate.groupName,
          inventorySortUpdate.headerName,
          inventorySortUpdate.sortDirection,
        );
  }

  setSortApplied() {
    this.isSortApplied =
      this.sortSelection.fieldName !==
        InventoryWorksheetCountSortOptions.lineNum ||
      this.sortSelection.sortValue !== SortDirections.asc;
  }

  // HELPER FUNCS
  async ionViewWillLeave() {
    this.searchKey = '';

    if (this.routeParamsSub$) {
      this.routeParamsSub$.unsubscribe();
    }

    if (this.onlineSubscription) {
      this.onlineSubscription.unsubscribe();
    }
    if (this._tooltip) {
      this._tooltip.dismiss();
    }
    // Dismiss pdp overlay on page leave
    await this.modalController.getTop().then(modalFound => {
      if (modalFound) {
        modalFound.dismiss();
      }
    });
    // Dismiss complete inventory toast on page leave
    await this.toastController.getTop().then(toastFound => {
      if (toastFound) {
        toastFound.dismiss();
      }
    });

    // Need to reset selections to avoid action bar showing back up.
    this.inventoryWorksheetService.resetInventoryItemSelections(
      this.inventoryId,
    );

    this.inventoryHotkeyService.setPreviousId('');
    this.isNewSearchEnabledSubscription$?.unsubscribe();
  }

  async ionViewDidLeave() {
    // Dismiss product detail modal after page leave
    await this.modalController.getTop().then(modalFound => {
      if (modalFound) {
        modalFound.dismiss();
      }
    });
    this.inventoryWorksheetService.resetInventoryWorksheetCountSort(
      this.inventoryId,
    );
    this.inventoryWorksheetService.resetInventoryWorksheetEditSort(
      this.inventoryId,
    );
    this.resetSearch();
  }

  scrollToAllGroups(): void {
    const tabContainer = document.querySelector(
      '.tabs-container',
    ) as HTMLElement;
    const allTab = document.getElementById('all-groups-tab') as HTMLElement;

    if (tabContainer && allTab) {
      tabContainer.classList.add('add-bounce');
      tabContainer.scrollTo({
        left: allTab.offsetLeft,
        behavior: 'smooth',
      });
      setTimeout(() => {
        tabContainer.classList.remove('add-bounce');
      }, 500);
    }
  }

  scrollToSingleGroup(index: number): void {
    const tabContainer = document.querySelector(
      '.tabs-container',
    ) as HTMLElement;
    const tab = document.getElementById('tabs-' + index) as HTMLElement;

    if (tabContainer && tab) {
      tabContainer.classList.add('add-bounce');
      tabContainer.scrollTo({
        left: tab.offsetLeft - 50,
        behavior: 'smooth',
      });
      setTimeout(() => {
        tabContainer.classList.remove('add-bounce');
      }, 500);
    }
  }
}
